import { Editor, Node, Path, Element as SlateElement, Text, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';
import { Constants } from './constants';
import { VeltService } from './velt';
export class EditorService {
    constructor(plugin) {
        this.getSelectedText = (editor) => {
            try {
                // Ensure there's a selection
                if (!editor.selection)
                    return null;
                // Extract and return the selected text
                return Editor.string(editor, editor.selection);
            }
            catch (err) {
                VeltService.catchError('Error getting selected text:', err);
                return null;
            }
        };
        /**
         * Finds occurrences of a given text within a specified element or text
         * @param {Object} options - Configuration options for occurrence search
         * @param {string} options.searchText - The text to search for
         * @param {SlateElement|string} [options.scopeElement] - DOM element or text to search within
         * @param {number} [options.startOffset] - Optional starting offset for the search
         * @param {boolean} [options.caseSensitive] - Whether the search should be case-sensitive
         * @returns {Object} Occurrence details
         */
        this.findOccurrenceOfText = (options) => {
            try {
                const { searchText, scopeElement, startOffset = 0, caseSensitive = true } = options;
                // Validate input
                if (!searchText)
                    return { total: 0, position: 0 };
                // Determine the text to search in
                const fullText = typeof scopeElement === 'string' ? scopeElement : (scopeElement === null || scopeElement === void 0 ? void 0 : scopeElement.textContent) || '';
                // Adjust search based on case sensitivity
                const searchMethod = caseSensitive
                    ? (text) => text
                    : (text) => text.toLowerCase();
                const processedFullText = searchMethod(fullText);
                const processedSearchText = searchMethod(searchText);
                // Find all occurrences
                const occurrences = [];
                let lastIndex = 0;
                while (true) {
                    const matchIndex = processedFullText.indexOf(processedSearchText, lastIndex);
                    if (matchIndex === -1)
                        break;
                    occurrences.push(matchIndex);
                    lastIndex = matchIndex + processedSearchText.length;
                }
                // Calculate position
                const positionBeforeSelected = occurrences.filter((index) => index < startOffset).length + 1;
                return {
                    total: occurrences.length,
                    position: positionBeforeSelected,
                };
            }
            catch (error) {
                VeltService.catchError('Error finding occurrence of text:', error);
                return null;
            }
        };
        this.findOccurrenceOfSelectedText = (editor, scopeElement) => {
            var _a;
            try {
                // Get selected text
                const selectedText = this.getSelectedText(editor);
                // If no text selected, return 0
                if (!selectedText)
                    return 0;
                if (!scopeElement) {
                    // Determine which occurrence of the text this is within the document
                    // This helps with maintaining comment positions during text edits
                    let occurrence = 0;
                    const allMatches = this.findAllMatches(editor, selectedText);
                    const selection = editor.selection;
                    if (selection) {
                        // Check if selection is forward or backward
                        const isBackward = Path.compare(selection.anchor.path, selection.focus.path) > 0 ||
                            (Path.equals(selection.anchor.path, selection.focus.path) &&
                                selection.anchor.offset > selection.focus.offset);
                        const matchIndex = allMatches.findIndex((match) => {
                            // For backward selection, we need to swap anchor and focus in our comparison
                            if (isBackward) {
                                return (match.anchor.path.toString() === selection.focus.path.toString() &&
                                    match.focus.path.toString() === selection.anchor.path.toString() &&
                                    match.anchor.offset === selection.focus.offset &&
                                    match.focus.offset === selection.anchor.offset);
                            }
                            else {
                                return (match.anchor.path.toString() === selection.anchor.path.toString() &&
                                    match.focus.path.toString() === selection.focus.path.toString() &&
                                    match.anchor.offset === selection.anchor.offset &&
                                    match.focus.offset === selection.focus.offset);
                            }
                        });
                        if (matchIndex !== -1) {
                            occurrence = matchIndex + 1;
                        }
                    }
                    return occurrence;
                }
                // Find the DOM node of the selection
                const domSelection = ReactEditor.toDOMNode(editor, editor);
                const startOffset = (_a = editor.selection) === null || _a === void 0 ? void 0 : _a.anchor.offset;
                // Find occurrence
                const result = this.findOccurrenceOfText({
                    searchText: selectedText,
                    scopeElement: scopeElement || domSelection,
                    startOffset,
                });
                return result === null || result === void 0 ? void 0 : result.position;
            }
            catch (error) {
                VeltService.catchError('Error finding occurrence of selected text:', error);
                return null;
            }
        };
        this.findAllMatches = (editor, textToSearch, options = { caseSensitive: true }) => {
            try {
                const { caseSensitive = true } = options;
                const matches = [];
                // If no text to search, return empty array
                if (!textToSearch)
                    return matches;
                // Safely get text from a node
                const getText = (node) => {
                    return typeof node === 'object' && node !== null && 'text' in node ? node.text : '';
                };
                // Check if a node is a text node
                const isTextNode = (node) => {
                    return (typeof node === 'object' &&
                        node !== null &&
                        'text' in node &&
                        typeof node.text === 'string');
                };
                // Iterate through all text nodes in the document
                const searchInNode = (node, path) => {
                    // Handle text nodes
                    if (isTextNode(node)) {
                        const text = getText(node);
                        // Apply case sensitivity
                        const searchText = caseSensitive ? textToSearch : textToSearch.toLowerCase();
                        const nodeText = caseSensitive ? text : text.toLowerCase();
                        // Find all matches in the current text node
                        let lastIndex = 0;
                        while (true) {
                            const index = nodeText.indexOf(searchText, lastIndex);
                            if (index === -1)
                                break;
                            // Create a range for this match
                            const range = {
                                anchor: { path, offset: index },
                                focus: { path, offset: index + textToSearch.length },
                            };
                            matches.push(range);
                            // Move the search forward
                            lastIndex = index + 1;
                        }
                    }
                    // Recursively search child nodes
                    if ('children' in node && Array.isArray(node.children)) {
                        node.children.forEach((child, index) => {
                            searchInNode(child, [...path, index]);
                        });
                    }
                };
                // Start the search from the root of the document
                try {
                    const [initialNode] = Editor.nodes(editor, {
                        at: [],
                        mode: 'highest',
                    });
                    if (initialNode) {
                        searchInNode(initialNode[0], initialNode[1]);
                    }
                }
                catch (error) {
                    VeltService.catchError('Error searching in document:', error);
                }
                return matches;
            }
            catch (error) {
                VeltService.catchError('Error finding all matches:', error);
                return [];
            }
        };
        this.findParentNodeWithIdForSelection = (editor) => {
            try {
                const { selection } = editor;
                if (!selection) {
                    return null;
                }
                const { anchor } = selection;
                const node = Node.get(editor, anchor.path);
                return this.findParentNodeWithId(editor, { node, path: anchor.path });
            }
            catch (error) {
                VeltService.catchError('Error finding parent node with ID for selection:', error);
                return null;
            }
        };
        this.findParentNodeWithId = (editor, options) => {
            var _a;
            try {
                let { node, path } = options || {};
                if (!node) {
                    return null;
                }
                if (path) {
                    node = Editor.node(editor, path)[0];
                }
                let domNode = null;
                let editorDOM = null;
                try {
                    // Convert Slate path to DOM node
                    domNode = ReactEditor.toDOMNode(editor, node);
                }
                catch (err) {
                    // handle error here
                    return null;
                }
                try {
                    // Get the editor's DOM element
                    editorDOM = ReactEditor.toDOMNode(editor, editor);
                }
                catch (err) {
                    // handle error here
                    return null;
                }
                // Default options
                const maxIterations = (_a = options === null || options === void 0 ? void 0 : options.maxIterations) !== null && _a !== void 0 ? _a : 100;
                const idPattern = options === null || options === void 0 ? void 0 : options.idPattern;
                let currentNode = domNode;
                let iterationCount = 0;
                while (currentNode && currentNode !== document.body && iterationCount < maxIterations) {
                    // Check if we're still within the editor's DOM
                    if (!editorDOM.contains(currentNode)) {
                        return null;
                    }
                    // Check ID based on optional pattern or just presence of ID
                    if (currentNode.id) {
                        if (!idPattern || idPattern.test(currentNode.id)) {
                            return currentNode;
                        }
                    }
                    currentNode = currentNode.parentElement;
                    iterationCount++;
                }
                return null;
            }
            catch (error) {
                VeltService.catchError('Error finding parent node with ID:', error);
                return null;
            }
        };
        this.removeVeltCommentFromEditor = (editor, annotationId) => {
            try {
                // Validate input
                if (!editor || !annotationId) {
                    throw new Error('Invalid input: editor and annotationId are required');
                }
                // Create an array to store paths of elements to remove
                const elementsToUnwrap = [];
                // Iterate through all nodes in the editor
                for (const [node, path] of Node.descendants(editor)) {
                    // Check if the node is an element using the type
                    if (EditorService.isVeltCommentText(node) &&
                        node.annotationId === annotationId) {
                        // Store the path of the element to be removed
                        elementsToUnwrap.push(path);
                    }
                }
                try {
                    // Unwrap elements in reverse order to avoid path shifting issues
                    elementsToUnwrap.reverse().forEach((path) => {
                        const nodeAtPath = Editor.node(editor, path)[0];
                        const elementAtNode = ReactEditor.toDOMNode(editor, nodeAtPath);
                        if (elementAtNode === null || elementAtNode === void 0 ? void 0 : elementAtNode.isConnected) {
                            const unwrapNodes = () => {
                                Transforms.unwrapNodes(editor, {
                                    at: path,
                                    // This ensures only the wrapper is removed, keeping its children
                                    mode: 'lowest',
                                });
                            };
                            const HistoryEditor = EditorService.getHistoryEditor();
                            if (HistoryEditor && typeof HistoryEditor.withoutSaving === 'function') {
                                HistoryEditor.withoutSaving(editor, () => {
                                    unwrapNodes();
                                });
                            }
                            else {
                                unwrapNodes();
                            }
                        }
                    });
                }
                catch (err) {
                    VeltService.catchError('Error unwrapping elements:', err);
                }
                return true;
            }
            catch (error) {
                VeltService.catchError('Error removing Velt comment:', error);
                return false;
            }
        };
        // Helper function to highlight text with velt comment
        this.highlightTextWithVeltComment = (editor, annotation) => {
            var _a;
            try {
                const { text, targetTextNodeId, occurrence } = ((_a = annotation === null || annotation === void 0 ? void 0 : annotation.context) === null || _a === void 0 ? void 0 : _a.textEditorConfig) || {};
                if (!text || !occurrence) {
                    return;
                }
                const isWrapped = this.injectTag(editor, text, occurrence, annotation.annotationId, targetTextNodeId);
                if (isWrapped) {
                    // Store the annotation data
                    if (annotation.annotationId) {
                        this.plugin.storeService.annotationStore.set(annotation.annotationId, {
                            annotation,
                        });
                    }
                }
            }
            catch (error) {
                VeltService.catchError('Error highlighting text with velt comment:', error);
            }
        };
        this.getEditorTextContent = (editor) => {
            try {
                return editor.children.map((node) => Node.string(node)).join('');
            }
            catch (error) {
                VeltService.catchError('Error getting editor text content:', error);
                return '';
            }
        };
        this.onSlateValueChange = ({ editor }) => {
            try {
                // Phase 1: Collect all annotations in the document
                const annotationsToCheck = this.collectDocumentAnnotations({ editor });
                // Early return if no annotations to process
                if (annotationsToCheck.size === 0)
                    return;
                // Phase 2: Process each annotation to check for changes
                const changedAnnotations = new Map();
                for (const [annotationId] of annotationsToCheck) {
                    // Get the stored annotation data
                    const storedData = this.plugin.storeService.annotationStore.get(annotationId);
                    if (!storedData)
                        continue;
                    // Find all nodes with this annotation ID
                    const nodes = Array.from(Editor.nodes(editor, { at: [] }))
                        .filter(([node]) => EditorService.isVeltCommentText(node))
                        .filter(([node]) => node.annotationId === annotationId)
                        .map(([node, path]) => ({
                        node: node,
                        pos: Editor.start(editor, path).offset,
                    }));
                    // Skip if no nodes found for this annotation
                    if (!nodes.length)
                        continue;
                    const changes = this.processAnnotationChanges({ editor, annotationId });
                    // Store changes if any detected
                    if (changes) {
                        changedAnnotations.set(annotationId, changes);
                    }
                }
                // Phase 3: Apply updates for any annotations that changed
                if (changedAnnotations.size > 0) {
                    this.updateVeltComments({ changes: changedAnnotations, editor });
                }
            }
            catch (error) {
                VeltService.catchError('Error in processVeltCommentChanges:', error);
            }
        };
        this.injectTag = (editor, textToSearch, occurrence, annotationId, targetElementId) => {
            try {
                // If target element ID is provided, find the DOM element
                let targetElement = null;
                if (targetElementId) {
                    targetElement = document.getElementById(targetElementId);
                    // Validate target element
                    if (!targetElement) {
                        return false;
                    }
                    // Ensure the element is part of the editor
                    if (!this.isElementInEditor(editor, targetElement)) {
                        return false;
                    }
                }
                else {
                    targetElement = ReactEditor.toDOMNode(editor, editor);
                }
                // If no target element is specified, fall back to editor-wide search
                if (!targetElement) {
                    return false;
                }
                // Find the range for the specific text occurrence
                const range = this.findTextOccurrence(editor, textToSearch, occurrence, targetElement);
                if (!range) {
                    return false;
                }
                // Check if the range is already wrapped with a velt-comment-text element
                const isAlreadyWrapped = Array.from(Editor.nodes(editor, {
                    at: range,
                    match: (node) => EditorService.isVeltCommentText(node),
                })).length > 0;
                if (isAlreadyWrapped)
                    return false;
                const wrapNodes = () => {
                    editor.wrapNodes({
                        type: Constants.ELEMENT_TYPE.VELT_COMMENT,
                        children: [],
                        annotationId: annotationId,
                    }, { at: range, split: true });
                };
                const HistoryEditor = EditorService.getHistoryEditor();
                // If history editor is available, use it to wrap nodes without saving
                if (HistoryEditor && typeof HistoryEditor.withoutSaving === 'function') {
                    HistoryEditor.withoutSaving(editor, () => {
                        wrapNodes();
                    });
                }
                else {
                    // Fallback if withoutSaving isn't available
                    // Just perform the operation directly
                    wrapNodes();
                }
                return true;
            }
            catch (error) {
                VeltService.catchError('Error injecting tag:', error);
                return false;
            }
        };
        this.plugin = plugin;
    }
    // Phase 1: Collect all annotations in the document
    collectDocumentAnnotations({ editor, }) {
        try {
            const annotationsToCheck = new Map();
            // Traverse all nodes in the document
            Array.from(Editor.nodes(editor, { at: [] }))
                .filter(([node]) => EditorService.isVeltCommentText(node))
                .forEach(([node]) => {
                const veltNode = node;
                const annotationId = veltNode.annotationId;
                if (annotationId) {
                    const storedData = this.plugin.storeService.annotationStore.get(annotationId);
                    if (storedData === null || storedData === void 0 ? void 0 : storedData.annotation) {
                        annotationsToCheck.set(annotationId, storedData.annotation);
                    }
                }
            });
            return annotationsToCheck;
        }
        catch (error) {
            VeltService.catchError('Error collecting document annotations:', error);
            return new Map();
        }
    }
    processAnnotationChanges({ editor, annotationId, }) {
        var _a, _b, _c, _d;
        try {
            let targetNode = null;
            let targetElement = null;
            let targetPath = null;
            const annotation = (_a = this.plugin.storeService.annotationStore.get(annotationId)) === null || _a === void 0 ? void 0 : _a.annotation;
            if (!((_b = annotation === null || annotation === void 0 ? void 0 : annotation.context) === null || _b === void 0 ? void 0 : _b.textEditorConfig)) {
                return null;
            }
            const { text, targetTextNodeId, occurrence } = ((_c = annotation === null || annotation === void 0 ? void 0 : annotation.context) === null || _c === void 0 ? void 0 : _c.textEditorConfig) || {};
            const annotatedNodes = Array.from(Editor.nodes(editor, {
                at: [],
                match: (node) => EditorService.isVeltCommentText(node) &&
                    node.annotationId === annotationId,
                mode: 'lowest',
            }));
            for (const [node, path] of annotatedNodes) {
                targetNode = node;
                targetPath = path;
            }
            if (!targetNode) {
                return null;
            }
            try {
                targetElement = ReactEditor.toDOMNode(editor, targetNode);
                if (!targetElement) {
                    return null;
                }
            }
            catch (error) {
                return null;
            }
            // Extract and concatenate text from matched nodes
            const currentText = annotatedNodes
                .map(([node]) => Node.string(node))
                .join('');
            if (!currentText) {
                return null;
            }
            const currentTargetNode = this.findParentNodeWithId(editor, {
                node: targetNode,
                path: targetPath !== null && targetPath !== void 0 ? targetPath : undefined,
            });
            let currentTargetNodeId;
            if (currentTargetNode) {
                currentTargetNodeId = currentTargetNode.id;
            }
            let containerNode;
            let containerPath;
            if (currentTargetNode) {
                containerNode = ReactEditor.toSlateNode(editor, currentTargetNode);
                containerPath = ReactEditor.findPath(editor, containerNode);
            }
            const currentOccurrence = (_d = this.findTextOccurrenceOfNode(editor, targetNode, targetPath !== null && targetPath !== void 0 ? targetPath : undefined, containerNode, containerPath)) === null || _d === void 0 ? void 0 : _d.nodeOccurrencePosition;
            if (!currentOccurrence) {
                return null;
            }
            const textChanged = currentText !== text;
            const occurrenceChanged = currentOccurrence !== occurrence;
            const targetTextNodeIdChanged = targetTextNodeId !== currentTargetNodeId;
            if (textChanged || occurrenceChanged || targetTextNodeIdChanged) {
                return {
                    contentChanged: textChanged,
                    currentText,
                    occurrenceChanged,
                    currentOccurrence: currentOccurrence !== null && currentOccurrence !== void 0 ? currentOccurrence : 0,
                    originalOccurrence: occurrence,
                    annotationId,
                    originalText: text,
                    originalTargetTextNodeId: targetTextNodeId,
                    newTargetTextNodeId: currentTargetNodeId,
                    annotation,
                    multiThreadAnnotationId: annotation === null || annotation === void 0 ? void 0 : annotation.multiThreadAnnotationId,
                    targetTextNodeIdChanged,
                };
            }
            return null;
        }
        catch (error) {
            VeltService.catchError('Error processing annotation changes:', error);
            return null;
        }
    }
    // Phase 3: Apply updates for any annotations that changed
    /**
     * Updates the Velt comment element with new annotation data
     */
    updateVeltComments({ changes, editor, }) {
        var _a;
        try {
            for (const [annotationId, changeData] of Array.from(changes.entries())) {
                try {
                    if (!((_a = changeData.annotation.context) === null || _a === void 0 ? void 0 : _a.textEditorConfig))
                        continue;
                    const textEditorConfig = JSON.parse(JSON.stringify(changeData.annotation.context.textEditorConfig));
                    if (changeData.contentChanged) {
                        textEditorConfig.text = changeData.currentText;
                    }
                    if (changeData.occurrenceChanged) {
                        textEditorConfig.occurrence = changeData.currentOccurrence;
                    }
                    if (changeData.targetTextNodeIdChanged) {
                        textEditorConfig.targetTextNodeId = changeData.newTargetTextNodeId;
                    }
                    const context = Object.assign(Object.assign({}, changeData.annotation.context), { textEditorConfig });
                    // Remove data from store
                    this.plugin.storeService.annotationStore.remove(annotationId);
                    this.removeVeltCommentFromEditor(editor, annotationId);
                    VeltService.updateContext({ annotationId, context });
                }
                catch (error) {
                    VeltService.catchError('Error updating Velt comments', error);
                }
            }
        }
        catch (error) {
            VeltService.catchError('Error updating Velt comments', error);
        }
    }
    isElementInEditor(editor, element) {
        try {
            // Convert Slate editor to its DOM representation
            const editorElement = ReactEditor.toDOMNode(editor, editor);
            // Check if the target element is a descendant of the editor
            return editorElement.contains(element);
        }
        catch (error) {
            VeltService.catchError('Error checking element in editor:', error);
            return false;
        }
    }
    // Helper method to extract the traversal logic
    traverseNodesForText(currentNode, currentPath, textNodePositions, fullText) {
        try {
            if (Text.isText(currentNode)) {
                const start = fullText.length;
                fullText += currentNode.text;
                const end = fullText.length;
                textNodePositions.push({
                    node: currentNode,
                    path: currentPath,
                    start,
                    end,
                });
            }
            else if (SlateElement.isElement(currentNode)) {
                // Traverse child nodes
                for (let i = 0; i < currentNode.children.length; i++) {
                    const childPath = [...currentPath, i];
                    fullText = this.traverseNodesForText(currentNode.children[i], childPath, textNodePositions, fullText);
                }
            }
            return fullText;
        }
        catch (error) {
            VeltService.catchError('Error traversing nodes for text:', error);
            return '';
        }
    }
    collectSubtreeText(editor, node, path) {
        try {
            const textNodePositions = [];
            let fullText = '';
            // Special case for the root node (editor itself)
            if (path.length === 0) {
                // When path is [], we're at the editor root, so traverse all children
                for (let i = 0; i < editor.children.length; i++) {
                    const childPath = [i];
                    fullText = this.traverseNodesForText(editor.children[i], childPath, textNodePositions, fullText);
                }
            }
            else {
                // Normal case for non-root nodes
                fullText = this.traverseNodesForText(node, path, textNodePositions, fullText);
            }
            return { fullText, textNodePositions };
        }
        catch (error) {
            VeltService.catchError('Error collecting subtree text:', error);
            return { fullText: '', textNodePositions: [] };
        }
    }
    findTextOccurrence(editor, textToSearch, occurrence, targetElement) {
        try {
            // Convert target element to Slate node
            const targetNode = ReactEditor.toSlateNode(editor, targetElement);
            const targetPath = ReactEditor.findPath(editor, targetNode);
            // Collect all text from the subtree
            const { fullText, textNodePositions } = this.collectSubtreeText(editor, targetNode, targetPath);
            // Find all matches in the full text
            const matches = [];
            let match;
            const regex = new RegExp(textToSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
            while ((match = regex.exec(fullText)) !== null) {
                matches.push({ index: match.index });
            }
            // If we don't have enough occurrences, return null
            if (matches.length < occurrence) {
                return null;
            }
            // Get the specific match
            const targetMatch = matches[occurrence - 1];
            // Find which text nodes the match spans
            const matchStart = targetMatch.index;
            const matchEnd = matchStart + textToSearch.length;
            // Find the range that covers this match
            let anchorNodeInfo;
            let focusNodeInfo;
            for (const nodePosition of textNodePositions) {
                // Check for anchor (start of match)
                if (matchStart >= nodePosition.start && matchStart < nodePosition.end) {
                    anchorNodeInfo = nodePosition;
                }
                // Check for focus (end of match)
                if (matchEnd > nodePosition.start && matchEnd <= nodePosition.end) {
                    focusNodeInfo = nodePosition;
                }
                // If we have both anchor and focus, we can create the range
                if (anchorNodeInfo && focusNodeInfo) {
                    return {
                        anchor: {
                            path: anchorNodeInfo.path,
                            offset: matchStart - anchorNodeInfo.start,
                        },
                        focus: {
                            path: focusNodeInfo.path,
                            offset: matchEnd - focusNodeInfo.start,
                        },
                    };
                }
            }
            return null;
        }
        catch (error) {
            VeltService.catchError('Error in findTextOccurrence:', error);
            return null;
        }
    }
    // for dynamic update
    /**
     * Find all occurrences of a specific text in the editor
     * @param {Editor} editor - The Slate.js editor instance
     * @param {string} searchText - The text to search for
     * @param {boolean} [caseSensitive=true] - Whether the search should be case-sensitive
     * @param {Node} [containerNode] - Optional container node to limit the search scope (if provided, only searches within this node)
     * @param {Path} [containerPath] - Optional path of the container node (will be calculated if not provided)
     * @returns {Array} Array of occurrence details
     */
    findAllTextOccurrences(editor, searchText, caseSensitive = true, containerNode, containerPath) {
        try {
            if (!searchText)
                return [];
            const occurrences = [];
            // Process search based on case sensitivity
            const processText = caseSensitive
                ? (text) => text
                : (text) => text.toLowerCase();
            const processedSearchText = processText(searchText);
            // Find container path if container node is provided but path isn't
            if (containerNode && !containerPath) {
                try {
                    for (const [n, p] of Editor.nodes(editor)) {
                        if (n === containerNode) {
                            containerPath = p;
                            break;
                        }
                    }
                }
                catch (error) {
                    VeltService.catchError('Error finding container path:', error);
                }
            }
            // Determine search scope
            const searchAt = containerPath || [];
            // Iterate through all text nodes in the scope
            for (const [node, path] of Editor.nodes(editor, {
                at: searchAt,
                match: (n) => Text.isText(n),
            })) {
                // Skip nodes that aren't within the container if container is specified
                if (containerPath &&
                    !Path.isDescendant(path, containerPath) &&
                    !Path.equals(path, containerPath)) {
                    continue;
                }
                const nodeText = Node.string(node);
                const processedNodeText = processText(nodeText);
                let lastIndex = 0;
                let matchIndex;
                // Find all occurrences within this text node
                while ((matchIndex = processedNodeText.indexOf(processedSearchText, lastIndex)) !== -1) {
                    // Create a point at the start of the match
                    const start = { path, offset: matchIndex };
                    // Create a point at the end of the match
                    const end = { path, offset: matchIndex + searchText.length };
                    // Create a range from start to end points
                    const range = { anchor: start, focus: end };
                    occurrences.push({
                        node,
                        path,
                        range,
                        startPoint: start,
                        endPoint: end,
                        text: nodeText.substring(matchIndex, matchIndex + searchText.length),
                    });
                    lastIndex = matchIndex + processedSearchText.length;
                }
            }
            return occurrences;
        }
        catch (error) {
            VeltService.catchError('Error finding all text occurrences:', error);
            return [];
        }
    }
    findTextOccurrenceOfNode(editor, node, nodePath, containerNode, containerPath) {
        try {
            // Get the text of the provided node
            const nodeText = Node.string(node);
            if (!nodeText.trim()) {
                // Return empty result for empty nodes
                return {
                    node,
                    nodePath,
                    text: nodeText,
                    totalOccurrences: 0,
                    nodeOccurrencePosition: 0,
                    textOccurrences: [],
                    containerNode,
                    containerPath,
                };
            }
            // Find the node's path if not provided
            if (!nodePath) {
                try {
                    // Try to find the path using ReactEditor if applicable
                    if ('findPath' in ReactEditor) {
                        try {
                            nodePath = ReactEditor.findPath(editor, node);
                        }
                        catch (reactError) {
                            VeltService.catchError('Error finding path with ReactEditor:', reactError);
                        }
                    }
                    // Fall back to manual search if needed
                    if (!nodePath) {
                        for (const [n, p] of Editor.nodes(editor)) {
                            if (n === node) {
                                nodePath = p;
                                break;
                            }
                        }
                    }
                }
                catch (error) {
                    VeltService.catchError('Error finding node path:', error);
                }
            }
            // Use findAllTextOccurrences to find all occurrences of this node's text
            // If containerNode is provided, search will be limited to that container
            const textOccurrences = this.findAllTextOccurrences(editor, nodeText, true, containerNode, containerPath);
            // Calculate occurrence position (1-based)
            let nodeOccurrencePosition = 0;
            if (nodePath) {
                // Find matching occurrence with the same path or where the node path is an ancestor
                for (let i = 0; i < textOccurrences.length; i++) {
                    const occurrence = textOccurrences[i];
                    // Check path relationships
                    const isEqual = Path.equals(occurrence.path, nodePath);
                    const isDescendant = Path.isAncestor(nodePath, occurrence.path);
                    if (isEqual || isDescendant) {
                        // Found the matching occurrence, using 1-based position index
                        nodeOccurrencePosition = i + 1;
                        break;
                    }
                }
            }
            // Create detailed occurrence info
            const occurrenceInfo = {
                node,
                nodePath,
                text: nodeText,
                totalOccurrences: textOccurrences.length,
                nodeOccurrencePosition,
                textOccurrences,
                containerNode,
                containerPath,
            };
            return occurrenceInfo;
        }
        catch (error) {
            VeltService.catchError('Error finding text occurrence of node:', error);
            return null;
        }
    }
}
EditorService.setHistoryEditor = (historyEditor) => {
    EditorService.HistoryEditor = historyEditor;
};
EditorService.getHistoryEditor = () => {
    return EditorService.HistoryEditor;
};
EditorService.getEditorId = (editor) => {
    var _a, _b;
    try {
        // Get the DOM node representing the editor
        const domNode = ReactEditor.toDOMNode(editor, editor);
        if (domNode) {
            return ((_b = (_a = domNode === null || domNode === void 0 ? void 0 : domNode.closest(`[${Constants.ATTRIBUTES.EDITOR_ID}]`)) === null || _a === void 0 ? void 0 : _a.getAttribute(Constants.ATTRIBUTES.EDITOR_ID)) !== null && _b !== void 0 ? _b : null);
        }
        // No parent with data-editor-id found
        return null;
    }
    catch (error) {
        // Handle cases where the editor might not be connected to the DOM
        VeltService.catchError('Error finding editor ID:', error);
        return null;
    }
};
EditorService.isVeltCommentText = (element) => {
    return element.type === Constants.ELEMENT_TYPE.VELT_COMMENT;
};
